import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Hash, Plus, Loader2, Settings2, Trash2, Check, X, Pencil, GripVertical, Bell, BellOff } from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useRealtime } from "@/context/RealtimeContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const MAX_TOPICS = 20;

// Horizontal Topics rail for a BLOQ — split the community into discussion areas (like Telegram
// topics / Discord channels). Each topic has its own conversation, unread badge, and (for managers)
// create/rename/delete controls.
export function BloqTopicsRail({ bloqId, activeTopicId, onSelect }) {
  const qc = useQueryClient();
  const [manageOpen, setManageOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [emoji, setEmoji] = useState("💬");

  const { data } = useQuery({
    queryKey: ["bloq-topics", bloqId],
    queryFn: () => api.bloqTopics(bloqId),
    enabled: !!bloqId,
    refetchInterval: 30000,
  });
  const topics = data?.topics || [];
  const canManage = !!data?.can_manage;

  const invalidate = () => qc.invalidateQueries({ queryKey: ["bloq-topics", bloqId] });

  // Live-resort when anyone (web or mobile) reorders this BLOQ's topics.
  const { subscribe } = useRealtime();
  useEffect(() => {
    return subscribe((msg) => {
      if (msg?.type === "circle.topic_reordered" && msg.circle_id === bloqId) invalidate();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscribe, bloqId]);

  const createMut = useMutation({
    mutationFn: (payload) => api.createBloqTopic(bloqId, payload),
    onSuccess: (t) => {
      invalidate();
      setCreating(false);
      setName("");
      setEmoji("💬");
      toast.success("Topic created");
      if (t?.conversation_id) onSelect(t);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Couldn't create topic"),
  });

  // Don't render the rail at all when there's nothing to split and the viewer can't manage.
  if (topics.length <= 1 && !canManage) return null;

  const activeId = activeTopicId || topics.find((t) => t.is_default)?.id;

  return (
    <div className="flex shrink-0 items-center gap-1.5 overflow-x-auto border-b border-white/8 bg-bloq-s1/40 px-3 py-2" data-testid="bloq-topics-rail">
      {topics.map((t) => {
        const active = t.id === activeId;
        return (
          <button
            key={t.id}
            onClick={() => onSelect(t)}
            className={cn(
              "relative flex shrink-0 items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors",
              active
                ? "border-bloq-mint/50 bg-bloq-mint/15 text-bloq-mint"
                : "border-white/10 bg-white/[0.03] text-bloq-muted hover:text-white",
            )}
            data-testid={`bloq-topic-${t.id}`}
          >
            <span>{t.emoji || "#"}</span>
            <span className="max-w-[120px] truncate">{t.name}</span>
            {t.unread > 0 && !active && (
              <span className="ml-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-bloq-mint px-1 text-[10px] font-bold text-bloq-bg" data-testid={`bloq-topic-unread-${t.id}`}>
                {t.unread}
              </span>
            )}
          </button>
        );
      })}
      {canManage && (
        <div className="flex shrink-0 items-center gap-1">
          <button
            onClick={() => setCreating(true)}
            disabled={topics.length >= MAX_TOPICS}
            className="flex h-8 w-8 items-center justify-center rounded-full border border-white/10 text-bloq-muted transition-colors hover:border-bloq-mint/40 hover:text-bloq-mint disabled:opacity-40"
            data-testid="bloq-topic-add-btn"
            title={topics.length >= MAX_TOPICS ? `Max ${MAX_TOPICS} topics` : "New topic"}
          >
            <Plus className="h-4 w-4" />
          </button>
          <button
            onClick={() => setManageOpen(true)}
            className="flex h-8 w-8 items-center justify-center rounded-full border border-white/10 text-bloq-muted transition-colors hover:text-white"
            data-testid="bloq-topic-manage-btn"
            title="Manage topics"
          >
            <Settings2 className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Create topic */}
      <Dialog open={creating} onOpenChange={(o) => !o && setCreating(false)}>
        <DialogContent className="max-w-sm border-white/10 bg-bloq-s1 text-white" data-testid="bloq-topic-create-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading"><Hash className="h-4 w-4 text-bloq-mint" /> New topic</DialogTitle>
            <DialogDescription className="text-bloq-muted">Give this discussion area a name — up to {MAX_TOPICS} per BLOQ.</DialogDescription>
          </DialogHeader>
          <div className="flex gap-2">
            <Input value={emoji} onChange={(e) => setEmoji(e.target.value.slice(0, 2))} className="h-11 w-16 rounded-xl border-white/12 bg-bloq-bg text-center text-xl" data-testid="bloq-topic-emoji-input" />
            <Input value={name} onChange={(e) => setName(e.target.value.slice(0, 40))} placeholder="e.g. Announcements" className="h-11 flex-1 rounded-xl border-white/12 bg-bloq-bg" data-testid="bloq-topic-name-input" autoFocus />
          </div>
          <DialogFooter>
            <Button
              onClick={() => name.trim() ? createMut.mutate({ name: name.trim(), emoji: emoji || "💬" }) : toast.error("Name is required")}
              disabled={createMut.isPending}
              className="w-full rounded-full bloq-gradient font-bold text-bloq-bg hover:opacity-90"
              data-testid="bloq-topic-create-submit"
            >
              {createMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create topic"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ManageTopicsDialog bloqId={bloqId} topics={topics} open={manageOpen} onOpenChange={setManageOpen} onChanged={invalidate} />
    </div>
  );
}

function ManageTopicsDialog({ bloqId, topics, open, onOpenChange, onChanged }) {
  const [editId, setEditId] = useState(null);
  const [val, setVal] = useState("");
  const [list, setList] = useState(topics);
  const [dragIdx, setDragIdx] = useState(null);

  useEffect(() => { setList(topics); }, [topics]);

  const renameMut = useMutation({
    mutationFn: ({ id, name }) => api.updateBloqTopic(bloqId, id, { name }),
    onSuccess: () => { onChanged(); setEditId(null); toast.success("Topic renamed"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Couldn't rename topic"),
  });
  const deleteMut = useMutation({
    mutationFn: (id) => api.deleteBloqTopic(bloqId, id),
    onSuccess: () => { onChanged(); toast.success("Topic deleted"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Couldn't delete topic"),
  });
  const reorderMut = useMutation({
    mutationFn: (order) => api.reorderBloqTopics(bloqId, order),
    onSuccess: () => { onChanged(); toast.success("Order updated"); },
    onError: (e) => { setList(topics); toast.error(e?.response?.data?.detail || "Couldn't reorder"); },
  });
  const muteMut = useMutation({
    mutationFn: ({ id, muted }) => api.muteBloqTopic(bloqId, id, muted),
    onSuccess: (_r, { muted }) => { onChanged(); toast.success(muted ? "Topic muted" : "Topic unmuted"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Couldn't update notifications"),
  });

  const onDrop = (dropIdx) => {
    if (dragIdx === null || dragIdx === dropIdx) { setDragIdx(null); return; }
    const next = [...list];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(dropIdx, 0, moved);
    setList(next);
    setDragIdx(null);
    reorderMut.mutate(next.map((t) => t.id));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm border-white/10 bg-bloq-s1 text-white" data-testid="bloq-topic-manage-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading"><Settings2 className="h-4 w-4 text-bloq-mint" /> Manage topics</DialogTitle>
          <DialogDescription className="text-bloq-muted">Drag the handle to reorder, or rename/remove. The default topic can't be deleted.</DialogDescription>
        </DialogHeader>
        <div className="max-h-[50vh] space-y-1.5 overflow-y-auto">
          {list.map((t, i) => (
            <div
              key={t.id}
              draggable={editId !== t.id}
              onDragStart={() => setDragIdx(i)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(i)}
              className={cn(
                "flex items-center gap-2 rounded-xl border border-white/8 bg-bloq-s2 px-2 py-2 transition-opacity",
                dragIdx === i && "opacity-50",
              )}
              data-testid={`manage-topic-${t.id}`}
            >
              <span className="cursor-grab text-bloq-muted active:cursor-grabbing" data-testid={`manage-topic-drag-${t.id}`}><GripVertical className="h-4 w-4" /></span>
              <span className="text-lg">{t.emoji || "#"}</span>
              {editId === t.id ? (
                <>
                  <Input value={val} onChange={(e) => setVal(e.target.value.slice(0, 40))} className="h-9 flex-1 rounded-lg border-white/12 bg-bloq-bg" autoFocus data-testid={`manage-topic-input-${t.id}`} />
                  <button onClick={() => val.trim() && renameMut.mutate({ id: t.id, name: val.trim() })} disabled={renameMut.isPending} className="rounded-lg bg-bloq-mint p-1.5 text-bloq-bg disabled:opacity-40" data-testid={`manage-topic-save-${t.id}`}><Check className="h-4 w-4" /></button>
                  <button onClick={() => setEditId(null)} className="rounded-lg bg-white/8 p-1.5"><X className="h-4 w-4" /></button>
                </>
              ) : (
                <>
                  <span className="min-w-0 flex-1 truncate text-sm font-medium">{t.name}{t.is_default && <span className="ml-1.5 text-[10px] uppercase text-bloq-muted">default</span>}</span>
                  <button onClick={() => muteMut.mutate({ id: t.id, muted: !t.muted })} disabled={muteMut.isPending} className={cn("rounded-lg p-1.5 hover:text-white disabled:opacity-40", t.muted ? "text-amber-400" : "text-bloq-muted")} data-testid={`manage-topic-mute-${t.id}`} title={t.muted ? "Unmute notifications" : "Mute notifications"}>
                    {t.muted ? <BellOff className="h-3.5 w-3.5" /> : <Bell className="h-3.5 w-3.5" />}
                  </button>
                  <button onClick={() => { setEditId(t.id); setVal(t.name); }} className="rounded-lg p-1.5 text-bloq-muted hover:text-white" data-testid={`manage-topic-rename-${t.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                  {!t.is_default && (
                    <button onClick={() => deleteMut.mutate(t.id)} disabled={deleteMut.isPending} className="rounded-lg p-1.5 text-bloq-muted hover:bg-destructive/15 hover:text-destructive disabled:opacity-40" data-testid={`manage-topic-delete-${t.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
